import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import {
  AlertTriangle, Search, RefreshCw, Plus, ArrowRight, Loader2,
  Briefcase, Tag, User as UserIcon, Truck, Filter, CheckCircle2, X, EyeOff
} from "lucide-react";

const KIND_META = {
  service: { label: "Servicio", icon: Briefcase, color: "text-violet-700", bg: "bg-violet-50 border-violet-200" },
  category: { label: "Categoría", icon: Tag, color: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
  personnel: { label: "Personal", icon: UserIcon, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  plate: { label: "Matrícula", icon: Truck, color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
};

function NormalizeModal({ open, item, onClose, onConfirm, busy }) {
  const [toValue, setToValue] = useState("");
  useEffect(() => { setToValue(item?.suggestion || ""); }, [item]);
  if (!open || !item) return null;
  const meta = KIND_META[item.kind];
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-2">
          <ArrowRight size={16} className={meta.color} /> Normalizar {meta.label.toLowerCase()}
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Sustituye <span className="font-mono font-semibold">"{item.raw_value}"</span> por el valor canónico
          en los {item.occurrences} usos detectados en {item.contract_name}.
        </p>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Valor canónico</label>
        <input
          autoFocus
          value={toValue}
          onChange={(e) => setToValue(e.target.value)}
          className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
          placeholder="Escribe el valor correcto…"
          data-testid="nc-normalize-input"
        />
        {item.suggestion && (
          <p className="text-xs text-slate-400 mt-1">Sugerencia: <span className="font-mono">{item.suggestion}</span></p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(toValue)}
            disabled={busy || !toValue.trim() || toValue.trim() === item.raw_value}
            className="px-5 py-2 text-sm font-semibold bg-[#007934] text-white rounded-lg hover:bg-[#005c27] shadow-md disabled:opacity-50 flex items-center gap-2"
            data-testid="nc-normalize-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Normalizar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NonCanonicalTab({ contracts = [] }) {
  const { authAxios } = useAuth();
  const [days, setDays] = useState(30);
  const [filterContract, setFilterContract] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ summary: {}, items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [normalizeItem, setNormalizeItem] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { days };
      if (filterContract) params.contract_id = filterContract;
      const r = await authAxios.get("/admin/non-canonical-values", { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudieron cargar los valores no canónicos");
    } finally {
      setLoading(false);
    }
  }, [authAxios, days, filterContract]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredItems = useMemo(() => {
    let items = data.items || [];
    if (filterKind) items = items.filter((x) => x.kind === filterKind);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((x) => (x.raw_value || "").toLowerCase().includes(q));
    }
    return items;
  }, [data.items, filterKind, search]);

  const addToCatalog = async (item) => {
    if (!(item.kind === "service" || item.kind === "category")) return;
    setBusy(true);
    try {
      const entity = item.kind === "service" ? "services" : "categories";
      await authAxios.post(`/contracts/${item.contract_id}/catalog/${entity}`, { value: item.raw_value });
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.detail || "No se pudo añadir al catálogo");
    } finally {
      setBusy(false);
    }
  };

  const normalize = async (toValue) => {
    if (!normalizeItem) return;
    setBusy(true);
    try {
      await authAxios.post("/admin/non-canonical-values/normalize", {
        contract_id: normalizeItem.contract_id,
        kind: normalizeItem.kind,
        from_value: normalizeItem.raw_value,
        to_value: toValue.trim(),
        days,
      });
      setNormalizeItem(null);
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.detail || "No se pudo normalizar");
    } finally {
      setBusy(false);
    }
  };

  const ignoreValue = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Ignorar "${item.raw_value}" en ${item.contract_name}?\nDejará de aparecer en este panel.`)) return;
    setBusy(true);
    try {
      await authAxios.post("/admin/non-canonical-values/ignore", {
        contract_id: item.contract_id,
        kind: item.kind,
        raw_value: item.raw_value,
      });
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.detail || "No se pudo ignorar");
    } finally {
      setBusy(false);
    }
  };

  const summaryCards = [
    { kind: "service", label: "Servicios", icon: Briefcase, color: "#7C3AED" },
    { kind: "category", label: "Categorías", icon: Tag, color: "#2563EB" },
    { kind: "personnel", label: "Personal", icon: UserIcon, color: "#059669" },
    { kind: "plate", label: "Matrículas", icon: Truck, color: "#D97706" },
  ];

  const summaryKey = { service: "services", category: "categories", personnel: "personnel", plate: "plates" };

  return (
    <>
      <NormalizeModal open={!!normalizeItem} item={normalizeItem} onClose={() => setNormalizeItem(null)} onConfirm={normalize} busy={busy} />

      <div className="space-y-4 animate-fade-in-up" data-testid="non-canonical-tab">
        {/* Header */}
        <div className="card-white p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500" /> Valores no canónicos detectados
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Servicios, categorías, personal o matrículas que aparecen en los partes pero no coinciden con
                los catálogos del contrato. Añádelos al catálogo o normalízalos en bloque.
              </p>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="text-xs font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              data-testid="nc-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refrescar
            </button>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {summaryCards.map(({ kind, label, icon: Icon, color }) => (
              <button
                key={kind}
                onClick={() => setFilterKind(filterKind === kind ? "" : kind)}
                className={`text-left p-4 rounded-xl border transition-all ${
                  filterKind === kind ? "border-slate-800 bg-slate-50 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`nc-summary-${kind}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Icon size={16} style={{ color }} />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
                </div>
                <p className="text-2xl font-black" style={{ color }}>{data.summary?.[summaryKey[kind]] || 0}</p>
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <select
              value={filterContract}
              onChange={(e) => setFilterContract(e.target.value)}
              className="form-input text-sm"
              data-testid="nc-filter-contract"
            >
              <option value="">Todos los contratos</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="form-input text-sm"
              data-testid="nc-filter-days"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={180}>Últimos 180 días</option>
            </select>
            <div className="md:col-span-2 relative">
              <Search size={14} className="absolute left-3 top-3 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar valor…"
                className="form-input pl-9 text-sm w-full"
                data-testid="nc-filter-search"
              />
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-3">
            <Filter size={11} className="inline mr-1" /> Mostrando {filteredItems.length} de {data.items?.length || 0} valores no canónicos
          </p>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">{error}</div>
        )}

        {/* List */}
        <div className="card-white overflow-hidden">
          {loading && filteredItems.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="animate-spin mx-auto mb-2" size={24} /> Cargando…
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <CheckCircle2 className="mx-auto mb-2 text-emerald-400" size={32} />
              <p className="font-semibold text-emerald-700">¡Sin valores no canónicos!</p>
              <p className="text-xs mt-1">Todos los partes del período coinciden con los catálogos.</p>
            </div>
          ) : (
            <table className="w-full data-table" data-testid="nc-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Valor detectado</th>
                  <th>Contrato</th>
                  <th>Ocurrencias</th>
                  <th>Sugerencia</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it) => {
                  const meta = KIND_META[it.kind];
                  const Icon = meta.icon;
                  return (
                    <tr key={`${it.contract_id}-${it.kind}-${it.raw_value}`} data-testid={`nc-row-${it.kind}-${it.raw_value}`}>
                      <td>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${meta.bg}`}>
                          <Icon size={11} className={meta.color} /> {meta.label}
                        </span>
                      </td>
                      <td>
                        <span className="font-mono font-semibold text-slate-800 text-sm">{it.raw_value}</span>
                        {it.report_dates?.[0] && (
                          <p className="text-xs text-slate-400 mt-0.5">Última vez: {it.report_dates[0]}</p>
                        )}
                      </td>
                      <td>
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: `${it.contract_color}1a`, color: it.contract_color }}
                        >
                          {it.contract_name}
                        </span>
                      </td>
                      <td className="font-bold text-slate-700">{it.occurrences}</td>
                      <td>
                        {it.suggestion ? (
                          <span className="text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">{it.suggestion}</span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">— sin coincidencia —</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          {(it.kind === "service" || it.kind === "category") && (
                            <button
                              onClick={() => addToCatalog(it)}
                              disabled={busy}
                              className="text-xs font-semibold text-violet-700 hover:bg-violet-50 px-2.5 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                              title="Añadir al catálogo del contrato"
                              data-testid={`nc-add-${it.kind}`}
                            >
                              <Plus size={12} /> Catálogo
                            </button>
                          )}
                          <button
                            onClick={() => setNormalizeItem(it)}
                            className="text-xs font-semibold text-slate-700 hover:bg-slate-100 px-2.5 py-1.5 rounded-lg flex items-center gap-1"
                            title="Sustituir por valor canónico en partes"
                            data-testid="nc-normalize-btn"
                          >
                            <ArrowRight size={12} /> Normalizar
                          </button>
                          <button
                            onClick={() => ignoreValue(it)}
                            disabled={busy}
                            className="text-xs font-semibold text-slate-500 hover:bg-slate-100 px-2.5 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                            title="Ocultar este valor del panel (no se modifican los partes)"
                            data-testid="nc-ignore-btn"
                          >
                            <EyeOff size={12} /> Ignorar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
